
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { 
  ArrowUpDown,
  Search,
  Download,
  DollarSign,
  Users,
  Percent
} from "lucide-react";

interface Strategy {
  name: string;
  exchange: string;
  aum: number;
  num_follower: number;
  pnl_ratio: number;
  timestamp: string;
}

interface StrategyTableProps {
  data: Strategy[];
}

const StrategyTable = ({ data }: StrategyTableProps) => {
  const [sortField, setSortField] = useState<keyof Strategy>('name');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [searchTerm, setSearchTerm] = useState('');
  
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };
  
  const formatPercent = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'percent',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value / 100);
  };
  
  const handleSort = (field: keyof Strategy) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };
  
  const filteredData = data.filter(strategy => 
    strategy.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    strategy.exchange.toLowerCase().includes(searchTerm.toLowerCase())
  );
  
  const sortedData = [...filteredData].sort((a, b) => {
    const fieldA = a[sortField];
    const fieldB = b[sortField];
    
    if (typeof fieldA === 'string' && typeof fieldB === 'string') {
      return sortDirection === 'asc' 
        ? fieldA.localeCompare(fieldB)
        : fieldB.localeCompare(fieldA);
    } else if (typeof fieldA === 'number' && typeof fieldB === 'number') {
      return sortDirection === 'asc' ? fieldA - fieldB : fieldB - fieldA;
    }
    
    // Fallback for any other type
    return 0;
  });
  
  const exportCsv = () => {
    // Generate CSV data
    const headers = ['Strategy Name', 'Exchange', 'AUM', 'Followers', 'P/L Ratio', 'Timestamp'];
    const csvRows = [
      headers.join(','),
      ...sortedData.map(row => [
        `"${row.name}"`,
        row.exchange,
        row.aum,
        row.num_follower,
        row.pnl_ratio,
        row.timestamp
      ].join(','))
    ];
    const csvContent = csvRows.join('\n');
    
    // Create download link
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', 'strategy_metrics.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div>
      <div className="flex gap-2 items-center justify-between mb-4">
        <div className="relative">
          <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search strategies..."
            className="pl-8 w-[250px]"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <Button variant="outline" onClick={exportCsv}>
          <Download className="h-4 w-4 mr-2" />
          Export CSV
        </Button>
      </div>
      
      <div className="rounded-md border overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[250px]">
                <Button variant="ghost" onClick={() => handleSort('name')}>
                  Strategy Name
                  <ArrowUpDown className="ml-2 h-4 w-4" />
                </Button>
              </TableHead>
              <TableHead>
                <Button variant="ghost" onClick={() => handleSort('exchange')}>
                  Exchange
                  <ArrowUpDown className="ml-2 h-4 w-4" />
                </Button>
              </TableHead>
              <TableHead className="text-right">
                <Button variant="ghost" onClick={() => handleSort('aum')}>
                  AUM
                  <ArrowUpDown className="ml-2 h-4 w-4" />
                </Button>
              </TableHead>
              <TableHead className="text-right">
                <Button variant="ghost" onClick={() => handleSort('num_follower')}>
                  Followers
                  <ArrowUpDown className="ml-2 h-4 w-4" />
                </Button>
              </TableHead>
              <TableHead className="text-right">
                <Button variant="ghost" onClick={() => handleSort('pnl_ratio')}>
                  P/L Ratio
                  <ArrowUpDown className="ml-2 h-4 w-4" />
                </Button>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sortedData.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="h-24 text-center">
                  No strategies found
                </TableCell>
              </TableRow>
            ) : (
              sortedData.map((strategy, index) => (
                <TableRow key={`${strategy.name}-${index}`}>
                  <TableCell className="font-medium">{strategy.name}</TableCell>
                  <TableCell>{strategy.exchange.toUpperCase()}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end">
                      <DollarSign className="h-3 w-3 text-green-600 mr-1" />
                      {formatCurrency(strategy.aum)}
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end">
                      <Users className="h-3 w-3 text-blue-600 mr-1" />
                      {strategy.num_follower.toLocaleString()}
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end">
                      <Percent className="h-3 w-3 text-orange-600 mr-1" />
                      <span className={strategy.pnl_ratio >= 0 ? 'text-green-600' : 'text-red-600'}>
                        {formatPercent(strategy.pnl_ratio)}
                      </span>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
      
      <div className="text-sm text-muted-foreground mt-4">
        Showing {sortedData.length} of {data.length} strategies
      </div>
    </div>
  );
};

export default StrategyTable;
